// Global Notification Listener
// ระบบแจ้งเตือนที่ทำงานในพื้นหลังตลอดเวลา
import { useEffect, useRef } from 'react';
import { db, collection, query, onSnapshot, where, orderBy, limit } from '../firebase';
import notificationSound from '../utils/notificationSound';
import { initFCM } from '../utils/fcmToken';
import { useToast } from '../components/common/Toast';
import { jobCategoryLabel } from '../utils/jobsCache';

// Helper to safely show notifications on both Desktop and Mobile
const showNotification = async (title, options) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    try {
        // Try ServiceWorker method first (required for Android Chrome)
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration && registration.showNotification) {
                await registration.showNotification(title, options);
                return;
            }
        }

        // Fallback to constructor (Desktop)
        // This might throw on Android Chrome if SW is not used, so we catch it
        new Notification(title, options);
    } catch (error) {
        console.error('Notification error:', error);
    }
};

export const useGlobalNotifications = (user, userData) => {
    const lastMessageCountsRef = useRef(new Map());
    const activeListenersRef = useRef(new Map());
    const fcmUnsubRef = useRef(null);
    // Track whether each listener has done its initial load (to avoid false positives)
    const initializedRef = useRef(new Set());
    // In-app toast for jobboard alerts. The toast API is memoized by ToastProvider, but keep
    // it in a ref so the listener effect below never re-subscribes because of it.
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;

    // Referentially-stable primitives for the effect deps below.
    // App.js mints a NEW userData object on every users/{uid} snapshot (FCM token
    // refresh, unread counters, etc.). Depending on the whole `userData` object made
    // the effect tear down & re-subscribe ALL listeners on every such write —
    // re-reading every chat's message history. Depend on stable primitives instead.
    const isAdmin = userData?.role === 'admin' || userData?.role === 'superAdmin';
    const hasUserData = !!userData;

    // ── FCM: Initialize when user logs in ──────────────────────────────────
    useEffect(() => {
        if (!user || user.isAnonymous) return;

        // Handle FCM foreground message (app is open)
        const handleForegroundMessage = (payload) => {
            const { title, body } = payload.notification || {};
            const data = payload.data || {};

            // Play sound
            notificationSound.play();

            // Show browser notification via SW (works on all platforms)
            showNotification(title || 'Fastlane LV', {
                body: body || 'มีการแจ้งเตือนใหม่',
                icon: '/logo192.png',
                badge: '/favicon-32x32.png',
                tag: data.chatId ? `fcm-${data.chatId}` : 'fcm-general',
                data: { url: data.url || '/' },
            });
        };

        let cancelled = false;
        initFCM(user, handleForegroundMessage).then((unsub) => {
            if (!unsub) return;
            if (cancelled) {
                unsub();
            } else {
                fcmUnsubRef.current = unsub;
            }
        });

        return () => {
            cancelled = true;
            if (fcmUnsubRef.current) {
                fcmUnsubRef.current();
                fcmUnsubRef.current = null;
            }
        };
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Cleanup FCM token on logout ─────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (!user && userData === null) {
                // user just logged out — remove token
            }
        };
    }, [user, userData]);

    useEffect(() => {
        if (!user || !userData) {
            // Clear all listeners when user logs out
            activeListenersRef.current.forEach(unsubscribe => unsubscribe());
            activeListenersRef.current.clear();
            lastMessageCountsRef.current.clear();
            initializedRef.current.clear();
            return;
        }

        const setupNotificationListeners = () => {
            console.log('🔔 Setting up global notification listeners...');

            // ─── Non-admin users: rely on FCM for new-message alerts ───
            // Both the user's own ChatWidget chat AND every booking chat are already
            // covered by the onNewChatMessage Cloud Function (FCM push, and the
            // foreground handler above plays the in-app sound). The old client listeners
            // here duplicated that — the booking one opened one listener PER booking
            // (unbounded per-user reads) — so they were removed, matching the admin
            // fan-out removal below. Unread state for the nav badge is handled separately
            // by useUnreadCounts and ChatWidget.

            // ─── Admin: listen to ALL chat activities ───
            if (isAdmin) {
                console.log('🔔 Setting up admin global listeners...');

                // (removed) Admin per-chat message-listener fan-out.
                // This opened the 40-recent-chats listener PLUS one live message listener
                // per thread (~40 standing listeners per admin) — a top driver of the
                // 200-800 concurrent-listener count. Every customer chat message is already
                // pushed by the onNewChatMessage Cloud Function (FCM), and the FCM foreground
                // handler (fcmToken.js) plays the in-app sound, so these were redundant.

                // ─── New-order detection ───
                // Scoped to docs created AFTER this session started — initial snapshot
                // returns 0 docs, so any subsequent 'added' change is genuinely a new order.
                // Avoids loading the entire bookings collection for every admin session.
                const sessionStartTs = new Date();
                // Per-admin isolation: a regular admin is alerted only for NEW orders that are
                // theirs (auto-assigned marketplace) or in the unclaimed pool — an unscoped
                // query is denied by the rules. superAdmin hears every new order.
                const B = collection(db, 'public/data/bookings');
                const newOrderQueries = (userData?.role === 'superAdmin')
                    ? [query(B, where('createdAt', '>', sessionStartTs))]
                    : [
                        query(B, where('assignedAdminId', '==', user?.uid), where('createdAt', '>', sessionStartTs)),
                        query(B, where('assignedAdminId', '==', null), where('createdAt', '>', sessionStartTs)),
                    ];
                const onNewOrderSnap = (snapshot) => {
                    snapshot.docChanges().forEach(change => {
                        if (change.type !== 'added') return;
                        const order = change.doc.data();
                        // Jobboard bookings are created by acceptJob/assignJob — the accepting
                        // admin already knows, and superAdmin's unscoped query would ring on
                        // EVERY accept. Skip them for every role, except an admin who was
                        // assigned the job by someone else (superAdmin assign/reassign).
                        if (order.source === 'jobboard') {
                            if (order.assignedAdminId !== user?.uid || order.jobAssignedBy === user?.uid) return;
                            const jobLabel = order.jobTitle || order.serviceName || 'งานจากบอร์ด';
                            const budgetPart = order.jobType === 'budget' && order.finalPrice ? ` — ตามงบ ฿${Number(order.finalPrice).toLocaleString()}` : '';
                            // In-app toast + sound only — assignJob already sends the FCM push
                            // (OS notification), so a showNotification here would double it.
                            notificationSound.play();
                            toastRef.current?.info(`📋 ได้รับมอบหมายงานจากบอร์ด — ${order.gameName ? order.gameName + ' · ' : ''}${jobLabel}${budgetPart}`, 6000);
                            return;
                        }
                        const isMarketplace = order.orderType === 'marketplace';
                        const price = (order.finalPrice || order.totalPrice || 0).toLocaleString();
                        const serviceName = order.serviceName || order.itemName || 'ออเดอร์ใหม่';
                        console.log('🆕 New order detected:', change.doc.id);
                        notificationSound.play();
                        showNotification(isMarketplace ? '🛒 ออเดอร์ Marketplace ใหม่' : '🆕 ออเดอร์บริการใหม่', {
                            body: `${serviceName} — ฿${price}\nลูกค้า: ${order.userEmail || '-'}`,
                            icon: '/logo192.png',
                            badge: '/favicon-32x32.png',
                            tag: `new-order-${change.doc.id}`,
                            data: { url: '/admin' },
                        });
                    });
                };
                newOrderQueries.forEach((q, idx) => {
                    const unsubNewOrders = onSnapshot(q, onNewOrderSnap, (error) => {
                        console.error('Error listening to new orders:', error);
                    });
                    activeListenersRef.current.set('admin-new-orders-' + idx, unsubNewOrders);
                });

                // ─── New jobboard jobs (session-scoped) ───
                // Same pattern as new-order detection: only jobs published AFTER this session
                // started, so the initial snapshot is empty and costs ~0 reads. desc + limit(20)
                // keeps the window bounded (the public jobs list rule caps limit at 21), and only
                // 'added' changes alert — a job being taken is a 'modified'/'removed' change.
                // publishedAt is re-stamped on reopen, so a job coming back to the board alerts too.
                const notifiedJobs = new Set();
                const newJobsQuery = query(
                    collection(db, 'public/data/jobs'),
                    where('publishedAt', '>', sessionStartTs),
                    orderBy('publishedAt', 'desc'),
                    limit(20)
                );
                const unsubNewJobs = onSnapshot(newJobsQuery, (snapshot) => {
                    snapshot.docChanges().forEach(change => {
                        if (change.type !== 'added') return;
                        const job = change.doc.data();
                        if (job.status && job.status !== 'open') return;
                        // A doc can re-enter the limit(20) window; alert once per publish.
                        const key = `${change.doc.id}:${job.publishedAt?.toMillis?.() || ''}`;
                        if (notifiedJobs.has(key)) return;
                        notifiedJobs.add(key);
                        const head = job.reopened ? '♻️ งานกลับเข้าบอร์ด' : '📌 งานใหม่';
                        const scope = [job.gameName, jobCategoryLabel(job)].filter(Boolean).join(' · ');
                        const budgetPart = job.jobType === 'budget' && job.budget ? ` · ตามงบ ฿${Number(job.budget).toLocaleString()}` : '';
                        // Toast + sound only: reviewJob approve / the reopen mirror already push FCM
                        // "📌 งานใหม่" to every admin (OS notification via the FCM handler).
                        notificationSound.play();
                        const titlePart = job.title ? ` · "${String(job.title).slice(0, 80)}"` : '';
                        toastRef.current?.info(`${head}${scope ? ' — ' + scope : ''}${budgetPart}${titlePart}`, 6000);
                    });
                }, (error) => {
                    // Rules/index not deployed yet, or jobboard disabled → stay silent.
                    console.error('Error listening to new jobs:', error);
                });
                activeListenersRef.current.set('admin-new-jobs', unsubNewJobs);

                // ─── Active-order chat notifications ───
                // Removed the per-active-booking message fan-out (previously read the
                // whole active-bookings set + a listener per booking ≈ 500+ reads per
                // admin session, and double-listened with the all-chats block above).
                // Booking chats live in the `chats` collection, so recent ones are
                // already covered by the all-chats(40) listener above, and the
                // onNewChatMessage Cloud Function pushes FCM for EVERY booking message
                // (foreground handler plays the in-app sound). Unread state is also
                // surfaced by the unreadByAdmin flag (useUnreadCounts badge + order list).
            }
        };

        // Initialize notification permission
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                console.log('Notification permission:', permission);
            });
        }

        // Set up listeners
        setupNotificationListeners();

        // Cleanup function
        return () => {
            console.log('🔇 Cleaning up global notification listeners...');
            activeListenersRef.current.forEach(unsubscribe => unsubscribe());
            activeListenersRef.current.clear();
            lastMessageCountsRef.current.clear();
            initializedRef.current.clear();
        };
        // Depend on stable primitives, NOT the whole userData object, so benign
        // user-doc writes don't tear down & re-read every chat listener.
    }, [user?.uid, isAdmin, hasUserData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Return cleanup function for external use
    return () => {
        activeListenersRef.current.forEach(unsubscribe => unsubscribe());
        activeListenersRef.current.clear();
        lastMessageCountsRef.current.clear();
        initializedRef.current.clear();
    };
};